import React, { FunctionComponent, useCallback, useMemo } from 'react';

import { connect, ConnectedProps, useSelector } from 'react-redux';

import { RootState } from '../../../../../../init-store';
import I18N from '../../../../components/i18n';
import Link from '../../../../components/link';
import { Select } from '../../../../components/select';
import { supportsDeviceChange } from '../../../../helpers/browser-capabilities';
import { i18n } from '../../../../helpers/i18n';
import { ACTION, FEATURE } from '../../../../helpers/logger-enums';
import { createLogger } from '../../../../helpers/loggerFacade';
import { isMessengerIntegration } from '../../../config/selectors';
import {
    getAudioInputDeviceSelector,
    getAudioOutputDeviceSelector,
    getDevicesByTypeSelector,
    getIsOutputDeviceChangeAvailable,
    selectIsNoiseCancellationEnabled,
    setAudioInputDeviceAction,
    setAudioOutputDeviceAction,
} from '../../../settings';
import { isMobileUa } from '../../../uatraits/selectors';
import { JoinWithMicrophoneOffSwitch } from '../join-with-microphone-off-switch';
import { NoiseCancellationSwitch } from '../noise-cancellation-switch';
import css from './camera.css';

const mapStateToProps = (state: RootState) => {
    return {
        inputDevices: getDevicesByTypeSelector(state, 'audioinput'),
        currentInputDeviceId: getAudioInputDeviceSelector(state)?.deviceId,

        outputDevices: getDevicesByTypeSelector(state, 'audiooutput'),
        currentOutputDeviceId: getAudioOutputDeviceSelector(state)?.deviceId,
        isOutputDeviceChangeAvailable: getIsOutputDeviceChangeAvailable(state),

        supportsDeviceChange: supportsDeviceChange(),
        isMobile: isMobileUa(state),
        showJoinWithMicrophoneOffSwitch: !isMessengerIntegration(state),
    };
};

const dispatchProps = {
    setAudioInputDeviceAction,
    setAudioOutputDeviceAction,
};

const connector = connect(mapStateToProps, dispatchProps);

export type SoundSectionProps = ConnectedProps<typeof connector>;

const logger = createLogger('settings-modal-sound');

const SoundSection: FunctionComponent<SoundSectionProps> = ({
    inputDevices,
    currentInputDeviceId,
    setAudioInputDeviceAction,

    outputDevices,
    currentOutputDeviceId,
    setAudioOutputDeviceAction,
    isOutputDeviceChangeAvailable,

    supportsDeviceChange,
    isMobile,
    showJoinWithMicrophoneOffSwitch,
}) => {
    const isNoiseCancellationEnabled = useSelector(selectIsNoiseCancellationEnabled);

    const inputOptions = useMemo(() => {
        if (isMobile) {
            return [
                {
                    itemKey: 'default',
                    label: i18n('settings', 'same-as-system'),
                },
            ];
        }
        return inputDevices.map(({ deviceId, label }) => ({
            itemKey: deviceId,
            label: label,
        }));
    }, [inputDevices, isMobile]);

    const setInputDevice = useCallback(
        (key: React.Key | null) => {
            logger.startFeatureDelta({
                feature: FEATURE.DEVICES,
                action: ACTION.SELECT_MIC,
            });
            setAudioInputDeviceAction(String(key));
        },
        [setAudioInputDeviceAction]
    );

    const outputOptions = useMemo(() => {
        return outputDevices.map(({ deviceId, label }) => ({
            itemKey: deviceId,
            label: label,
        }));
    }, [outputDevices]);

    const setAudioDevice = useCallback(
        (event) => {
            logger.startFeatureDelta({
                feature: FEATURE.DEVICES,
                action: ACTION.SELECT_SPEAKER,
            });
            setAudioOutputDeviceAction(event.target.label);
        },
        [setAudioOutputDeviceAction]
    );

    return (
        <>
            {supportsDeviceChange ? (
                <>
                    <h3 data-test-id="mic">{i18n('settings', 'mic')}</h3>
                    <Select
                        className={css.select}
                        items={inputOptions}
                        selectedKey={isMobile ? 'default' : currentInputDeviceId}
                        onSelectionChange={setInputDevice}
                        isDisabled={isMobile}
                    />
                    {isNoiseCancellationEnabled ? <NoiseCancellationSwitch /> : null}
                    {
                        // Скрываем опцию выбора аудио-выхода, если они тупо не доступны
                        isOutputDeviceChangeAvailable && outputOptions.length > 0 && (
                            <>
                                <h3 data-test-id="sound-output">{i18n('settings', 'sound-output')}</h3>
                                <Select
                                    className={css.select}
                                    items={outputOptions}
                                    selectedKey={currentOutputDeviceId}
                                    onSelectionChange={setAudioDevice}
                                />
                            </>
                        )
                    }
                    {showJoinWithMicrophoneOffSwitch && <JoinWithMicrophoneOffSwitch />}
                </>
            ) : (
                <div className={css.UnsupportedMessage}>
                    <I18N keyset="settings" loc="unsupported-microphone-change-message">
                        <Link data-ref="link-telemost" view="default" href="/download-desktop" target="_blank" />
                        <Link data-ref="link-yabro" view="default" href="https://browser.yandex.ru" target="_blank" />
                    </I18N>
                </div>
            )}
        </>
    );
};

export default connector(SoundSection);
